<template>
  <div class="asel">
    <section class="asel__filters" data-tour="admin-sellers-kpis">
      <button
        v-for="tab in statusTabs"
        :key="tab.key"
        type="button"
        class="sui-card asel__filter"
        :class="{ 'is-active': statusFilter === tab.key, 'is-alert': tab.key === 'PENDING' && tab.count > 0 }"
        @click="statusFilter = tab.key"
      >
        <span class="asel__filter-label">{{ tab.label }}</span>
        <span class="asel__filter-count">{{ tab.count }}</span>
      </button>
    </section>

    <section class="sui-card sui-card--flush" data-tour="admin-sellers-list">
      <div class="sui-toolbar" data-tour="admin-sellers-toolbar">
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input v-model.trim="keyword" type="search" placeholder="업체명 · 사업자번호 · 아이디 검색" />
        </label>
        <span class="sui-toolbar__spacer" />
        <button
          type="button"
          class="sui-btn sui-btn--primary"
          data-tour="admin-sellers-create"
          @click="openCreate"
        >
          <AppIcon icon="mdi-plus" :size="16" />
          판매업체 등록
        </button>
        <button type="button" class="sui-btn" :disabled="loading" @click="load">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="asel__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
        <button type="button" class="sui-btn sui-btn--sm" @click="load">다시 시도</button>
      </p>

      <div v-if="filtered.length" class="sui-table-wrap">
        <table class="sui-table">
          <thead>
            <tr>
              <th>업체</th>
              <th>사업자</th>
              <th>연락처</th>
              <th class="is-num">등록 상품</th>
              <th class="is-center">온보딩</th>
              <th class="is-center">상태</th>
              <th class="is-center">처리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="seller in filtered" :key="seller.sellerCode">
              <td>
                <span class="sui-table__strong">{{ seller.storeName || seller.sellerId }}</span>
                <span class="sui-table__sub">{{ seller.sellerCode }} · {{ seller.sellerId }}</span>
              </td>
              <td>
                <span>{{ seller.bizName || (seller.mustOnboard ? '온보딩 대기' : '—') }}</span>
                <span class="sui-table__sub">
                  <template v-if="seller.bizNumber || seller.ownerName">
                    {{ seller.bizNumber || '—' }} · {{ seller.ownerName || '—' }}
                  </template>
                  <template v-else>—</template>
                </span>
              </td>
              <td>
                <span>{{ seller.phone || '—' }}</span>
                <span class="sui-table__sub">{{ seller.email || '—' }}</span>
              </td>
              <td class="is-num">
                <router-link
                  v-if="seller.productCount > 0"
                  class="asel__count-link"
                  :to="{
                    path: '/admin/products',
                    query: { keyword: seller.storeName || seller.sellerCode },
                  }"
                >
                  {{ seller.productCount }}개
                </router-link>
                <span v-else>0개</span>
              </td>
              <td class="is-center">
                <span
                  class="sui-badge"
                  :class="seller.mustOnboard ? 'sui-badge--amber' : 'sui-badge--green'"
                >
                  {{ seller.mustOnboard ? '미완료' : '완료' }}
                </span>
                <button
                  type="button"
                  class="asel__sign-link"
                  data-tour="admin-sellers-contract"
                  @click="showAccepts(seller)"
                >
                  {{ seller.mustOnboard ? '계약서' : '계약·서명' }}
                </button>
              </td>
              <td class="is-center">
                <span class="sui-badge" :class="statusTone(seller.status)">
                  {{ SELLER_STATUS_LABEL[seller.status] }}
                </span>
                <span v-if="seller.rejectReason" class="asel__reason">{{ seller.rejectReason }}</span>
              </td>
              <td class="is-center">
                <div class="asel__actions">
                  <button
                    v-if="seller.status !== 'APPROVED'"
                    type="button"
                    class="sui-btn sui-btn--sm sui-btn--primary"
                    :disabled="Boolean(seller.mustOnboard)"
                    :title="seller.mustOnboard ? '온보딩 완료 후 승인 가능' : ''"
                    @click="onApprove(seller)"
                  >
                    승인
                  </button>
                  <button
                    v-if="seller.status !== 'REJECTED'"
                    type="button"
                    class="sui-btn sui-btn--sm"
                    @click="onReject(seller)"
                  >
                    반려
                  </button>
                  <button
                    v-if="seller.status === 'APPROVED'"
                    type="button"
                    class="sui-btn sui-btn--sm sui-btn--ghost"
                    @click="onSuspend(seller)"
                  >
                    정지
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-storefront-outline" :size="32" />
        <p>{{ loading ? '불러오는 중입니다…' : '조건에 맞는 판매업체가 없습니다.' }}</p>
      </div>
    </section>

    <div v-if="createOpen" class="asel__modal" role="dialog" aria-modal="true">
      <div class="asel__modal-backdrop" @click="createOpen = false" />
      <form class="asel__modal-panel" @submit.prevent="submitCreate">
        <header>
          <h2>판매업체 등록</h2>
          <button type="button" class="sui-btn sui-btn--ghost" @click="createOpen = false">닫기</button>
        </header>
        <p class="asel__modal-lead">
          아이디와 임시 비밀번호만 등록합니다. 사업자·정산·약관·서명은 업체가 로그인 후 온보딩에서
          입력합니다.
        </p>
        <div class="asel__modal-grid">
          <label>
            <span>아이디 *</span>
            <input
              v-model.trim="createForm.sellerId"
              required
              maxlength="20"
              placeholder="영문·숫자·_ 4~20자"
              pattern="[A-Za-z0-9_]{4,20}"
            />
          </label>
          <label>
            <span>임시 비밀번호 *</span>
            <input v-model="createForm.tempPassword" type="text" required minlength="8" />
          </label>
        </div>
        <footer>
          <button type="button" class="sui-btn" @click="createOpen = false">취소</button>
          <button type="submit" class="sui-btn sui-btn--primary" :disabled="creating">
            {{ creating ? '등록 중…' : '등록' }}
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  changeSellerStatus,
  createAdminSeller,
  fetchAdminLegalDocs,
  fetchAdminSellers,
  fetchSellerAcceptances,
  type AdminSellerRow,
} from '@/api/admin'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { SELLER_STATUS_LABEL, type SellerStatus } from '@/constants/sellerSession'
import {
  mergeLegalDocViews,
  openLegalAcceptancesPreview,
} from '@/utils/legalAcceptancePreview'

const { notify } = useAdminProducts()

const sellers = ref<AdminSellerRow[]>([])
const loading = ref(false)
const errorMessage = ref('')
const keyword = ref('')
const statusFilter = ref<string>('all')
const createOpen = ref(false)
const creating = ref(false)
const createForm = reactive({
  sellerId: '',
  tempPassword: '',
})

function sellerLabel(seller: AdminSellerRow) {
  return seller.storeName || seller.sellerId || seller.sellerCode
}

const STATUS_TONE: Record<SellerStatus, string> = {
  PENDING: 'sui-badge--violet',
  APPROVED: 'sui-badge--green',
  REJECTED: 'sui-badge--red',
  SUSPENDED: 'sui-badge--amber',
  CLOSED: 'sui-badge--gray',
}

function statusTone(status: SellerStatus): string {
  return STATUS_TONE[status] ?? 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''

  try {
    sellers.value = await fetchAdminSellers()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '판매업체를 불러오지 못했습니다.'
    sellers.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)

const statusTabs = computed(() => {
  const countOf = (key: SellerStatus | 'all') =>
    key === 'all' ? sellers.value.length : sellers.value.filter((row) => row.status === key).length

  return [
    { key: 'all', label: '전체', count: countOf('all') },
    { key: 'PENDING', label: '승인 대기', count: countOf('PENDING') },
    { key: 'APPROVED', label: '이용중', count: countOf('APPROVED') },
    { key: 'SUSPENDED', label: '정지', count: countOf('SUSPENDED') },
    { key: 'REJECTED', label: '반려', count: countOf('REJECTED') },
    { key: 'CLOSED', label: '해지', count: countOf('CLOSED') },
  ]
})

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()

  return sellers.value.filter((seller) => {
    if (statusFilter.value !== 'all' && seller.status !== statusFilter.value) return false
    if (!query) return true
    return (
      String(seller.storeName || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.bizName || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.bizNumber || '').includes(query) ||
      String(seller.sellerId || '')
        .toLowerCase()
        .includes(query) ||
      String(seller.sellerCode || '')
        .toLowerCase()
        .includes(query)
    )
  })
})

async function apply(seller: AdminSellerRow, status: string, reason = '') {
  try {
    await changeSellerStatus(seller.sellerCode, status, reason)
    await load()
    notify(`${sellerLabel(seller)} 상태를 바꿨습니다.`)
  } catch (error) {
    notify(error instanceof Error ? error.message : '상태를 바꾸지 못했습니다.')
  }
}

function makeTempPassword() {
  return `Tmp${Math.random().toString(36).slice(2, 8)}!a1`
}

function openCreate() {
  createForm.sellerId = ''
  createForm.tempPassword = makeTempPassword()
  createOpen.value = true
}

async function submitCreate() {
  creating.value = true
  try {
    const seller = await createAdminSeller({
      sellerId: createForm.sellerId,
      tempPassword: createForm.tempPassword,
    })
    createOpen.value = false
    await load()
    notify(
      `등록 완료. 아이디 ${seller.sellerId} / 임시비번 ${createForm.tempPassword}`,
    )
  } catch (error) {
    notify(error instanceof Error ? error.message : '등록에 실패했습니다.')
  } finally {
    creating.value = false
  }
}

async function showAccepts(seller: AdminSellerRow) {
  try {
    const [accepts, docs] = await Promise.all([
      fetchSellerAcceptances(seller.sellerCode),
      fetchAdminLegalDocs('SELLER'),
    ])
    const activeDocs = docs.filter((doc) => doc.active)
    const views = mergeLegalDocViews(accepts, activeDocs)
    if (!views.length) {
      window.alert('등록된 계약서·약관이 없습니다. 약관 CMS에서 활성 계약서를 등록해 주세요.')
      return
    }
    const signed = accepts.find((row) => row.signedAt || row.signature)
    const opened = openLegalAcceptancesPreview({
      heading: `${sellerLabel(seller)} 계약서`,
      subtitle: signed
        ? `${seller.sellerId} · 최근 동의 ${signed.signedAt || '-'}`
        : `${seller.sellerId} · 미서명 · 본문 열람`,
      accepts: views,
      party: {
        storeName: seller.storeName || seller.bizName,
        ownerName: seller.ownerName,
        bizNumber: seller.bizNumber,
        address: seller.address,
        addressDetail: seller.addressDetail,
      },
      downloadStoredPdf: async (row) => {
        if (!row.idx) throw new Error('저장된 계약서 PDF가 없습니다.')
        const { downloadSellerAcceptancePdf } = await import('@/api/admin')
        await downloadSellerAcceptancePdf(
          seller.sellerCode,
          row.idx,
          `${row.title || row.code}_v${row.version}.pdf`,
        )
      },
    })
    if (!opened) {
      notify('팝업이 차단되어 계약서를 열 수 없습니다.')
    }
  } catch (error) {
    notify(error instanceof Error ? error.message : '계약서를 불러오지 못했습니다.')
  }
}

function onApprove(seller: AdminSellerRow) {
  if (seller.mustOnboard) {
    notify('온보딩을 완료한 뒤에만 승인할 수 있습니다.')
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 승인할까요? 승인하면 상품 등록을 요청할 수 있습니다.`)) {
    return
  }
  apply(seller, 'APPROVED')
}

function onReject(seller: AdminSellerRow) {
  const reason = window.prompt('반려 사유를 적어 주세요.', seller.rejectReason || '')
  if (!reason?.trim()) {
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 반려할까요?`)) return
  apply(seller, 'REJECTED', reason.trim())
}

function onSuspend(seller: AdminSellerRow) {
  const reason = window.prompt('정지 사유를 적어 주세요.', '')
  if (!reason?.trim()) {
    return
  }
  if (!window.confirm(`${sellerLabel(seller)}을(를) 정지할까요?`)) return
  apply(seller, 'SUSPENDED', reason.trim())
}
</script>

<style lang="scss" src="./AdminSellersPage.scss"></style>
